import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, User, MessageCircle, Send, Camera, Image as ImageIcon, Smile, X, Loader2, Sparkles } from 'lucide-react';
import { CameraModal } from '../components/Common/CameraModal';
import { doc, getDoc, collection, query, where, getDocs, orderBy, addDoc, updateDoc, increment, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ShadowCard } from '../components/Feed/ShadowCard';
import { getAnonymousName, getAvatarForShadow } from '../utils/shadowIdentity';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const storage = getStorage();

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  let date;
  if (timestamp.toDate) date = timestamp.toDate();
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const CommentItem = ({ comment, shadow, onReply, isReply = false, depth = 0, onDeleteComment, onReplyAdded }) => {
   const [isRepliesExpanded, setIsRepliesExpanded] = useState(depth < 1);
   const [showQuickReply, setShowQuickReply] = useState(false);
   const [quickReplyText, setQuickReplyText] = useState('');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [selectedImageFile, setSelectedImageFile] = useState(null);
   const [selectedImagePreview, setSelectedImagePreview] = useState(null);
   const [showCamera, setShowCamera] = useState(false);
   const [showGifPicker, setShowGifPicker] = useState(false);
   const [gifSearchQuery, setGifSearchQuery] = useState('');
   const [gifResults, setGifResults] = useState([]);
   const [selectedGif, setSelectedGif] = useState(null);
   const [authorIsPremium, setAuthorIsPremium] = useState(false);

   useEffect(() => {
      let isMounted = true;
      if (comment.userId) {
         getDoc(doc(db, 'users', comment.userId)).then(snap => {
            if (snap.exists() && isMounted) {
               setAuthorIsPremium(snap.data().isPremium === true);
            }
         }).catch(console.error);
      }
      return () => { isMounted = false; };
   }, [comment.userId]);

   const trendingGifs = [
    "https://media.giphy.com/media/l41YtZOb9EUABnuqA/giphy.gif",
    "https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif",
    "https://media.giphy.com/media/cfuL5gqFDreXxkWQ4o/giphy.gif",
    "https://media.giphy.com/media/f31DK1KpGsyMU/giphy.gif",
   ];

   useEffect(() => {
      if (!showGifPicker) return;
      if (gifSearchQuery.trim() === '') {
          setGifResults(trendingGifs);
          return;
      }
      const fetchGifs = async () => {
          try {
              const apiKey = process.env.REACT_APP_GIPHY_API_KEY || 'MkLDt7E3pR3qJnqYvjnAbGRTjq5zmQ1g';
              const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(gifSearchQuery)}&limit=20`);
              if (res.ok) {
                 const data = await res.json();
                 const urls = data.data.map(r => r.images.fixed_height.url);
                 setGifResults(urls);
              } else {
                 setGifResults(trendingGifs);
              }
          } catch(e) {
              setGifResults(trendingGifs);
          }
      };
      const timeoutId = setTimeout(fetchGifs, 500);
      return () => clearTimeout(timeoutId);
   }, [gifSearchQuery, showGifPicker]);

   const fileInputRef = useRef(null);

   const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
         setSelectedImageFile(file);
         setSelectedImagePreview(URL.createObjectURL(file));
      }
   };
   
   const authorName = getAnonymousName(comment.userId);
   const authorAvatar = getAvatarForShadow(authorName);
   const isAuthor = shadow.userId === comment.userId;
   const isCommentOwner = auth.currentUser?.uid === comment.userId;
   const isShadowAuthor = shadow.userId === auth.currentUser?.uid;
   const canDelete = isCommentOwner || isShadowAuthor;

   const handleQuickReply = async () => {
       if ((!quickReplyText.trim() && !selectedImageFile && !selectedGif) || isSubmitting || !auth.currentUser) return;
       setIsSubmitting(true);
       
       let finalImageUrl = null;
       if (selectedImageFile) {
           const { ref: storageRef, uploadBytesResumable, getDownloadURL } = require('firebase/storage');
           const sRef = storageRef(storage, `shadow_comments/${Date.now()}_${selectedImageFile.name}`);
           const uploadTask = await uploadBytesResumable(sRef, selectedImageFile);
           finalImageUrl = await getDownloadURL(uploadTask.ref);
       }
       
       try {
           const parentId = comment.id;
            const newComment = {
               confessionId: shadow.id,
               parentId: parentId,
               userId: auth.currentUser.uid,
               content: quickReplyText.trim(),
               createdAt: serverTimestamp(),
               likes: 0,
               imageUrl: finalImageUrl,
               gif: selectedGif ? { url: selectedGif.url, id: Date.now().toString(), aspectRatio: 1 } : null
           };
           const docRef = await addDoc(collection(db, 'comments'), newComment);
           await updateDoc(doc(db, 'confessions', shadow.id), {
                commentCount: increment(1),
                latestComment: {
                    userId: auth.currentUser.uid,
                    content: quickReplyText.trim()
                }
            });
            
            // Notification Logic
            if (auth.currentUser.uid !== shadow.userId) {
                await addDoc(collection(db, 'users', shadow.userId, 'notifications'), {
                    type: 'comment',
                    fromUserId: auth.currentUser.uid,
                    confessionId: shadow.id,
                    commentId: docRef.id,
                    content: quickReplyText.trim(),
                    parentCommentId: parentId,
                    createdAt: new Date(),
                    read: false,
                    image: shadow.imageUrl || null
                });
            }
            
            if (parentId) {
                const parentSnap = await getDoc(doc(db, 'comments', parentId));
                if (parentSnap.exists() && parentSnap.data().userId !== auth.currentUser.uid && parentSnap.data().userId !== shadow.userId) {
                    await addDoc(collection(db, 'users', parentSnap.data().userId, 'notifications'), {
                        type: 'comment_reply',
                        fromUserId: auth.currentUser.uid,
                        confessionId: shadow.id,
                        commentId: docRef.id,
                        content: quickReplyText.trim(),
                        parentCommentId: parentId,
                        createdAt: new Date(),
                        read: false,
                        image: shadow.imageUrl || null
                    });
                }
            }
            
            toast.success("Reply added");

            setQuickReplyText('');
            setShowQuickReply(false);
            setSelectedImageFile(null);
            setSelectedImagePreview(null);
            setSelectedGif(null);
            setIsRepliesExpanded(true);
            if (onReplyAdded) {
               const addedComment = { id: docRef ? docRef.id : Date.now().toString(), ...newComment, createdAt: new Date(), replies: [] };
               onReplyAdded(parentId, addedComment);
            }
        } catch (err) {
           console.error("Error submitting quick reply", err);
       }
       setIsSubmitting(false);
   };
   
   return (
      <div className={`flex gap-3 ${isReply ? 'mt-4' : ''}`}>
        <div className={`${isReply ? 'w-8 h-8 mt-0.5' : 'w-10 h-10 mt-1'} rounded-full bg-gray-800 overflow-hidden flex items-center justify-center shrink-0 border border-gray-700`}>
          {authorAvatar ? <img src={authorAvatar} className="w-full h-full object-cover" alt="Avatar" /> : <User size={isReply ? 14 : 18} className="text-gray-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-bold text-[14px] ${isAuthor ? 'text-purple-400' : 'text-white'}`}>
              {authorName}
            </span>
            {authorIsPremium && (
               <Sparkles size={14} className="text-[#0BD9E5] shrink-0" />
            )}
            {isAuthor && (
               <span className="text-[10px] uppercase font-bold text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">OP</span>
            )}
            <span className="text-gray-500 text-[12px]">{formatRelativeTime(comment.createdAt)}</span>
          </div>
          <p className="text-gray-300 text-[15px] leading-relaxed whitespace-pre-wrap break-words overflow-hidden">{comment.content}</p>
          
          {comment.imageUrl && (
            <img src={comment.imageUrl} alt="Comment Attachment" className="mt-2 max-h-48 rounded-xl object-contain border border-gray-800" />
          )}
          {comment.gif && (
            <img src={comment.gif.url} alt="Comment GIF" style={{ aspectRatio: comment.gif.aspectRatio }} className="mt-2 max-h-48 rounded-xl object-contain border border-gray-800" />
          )}

          <div className="flex items-center gap-4 mt-2">
             <button onClick={() => setShowQuickReply(!showQuickReply)} className="text-gray-500 hover:text-white text-[13px] font-medium transition-colors">Reply</button>
              {canDelete && (
                 <button onClick={() => {
                     if (onDeleteComment) onDeleteComment(comment.id);
                     toast.success('Comment deleted');
                     (async () => {
                         try {
                             await deleteDoc(doc(db, 'comments', comment.id));
                             await updateDoc(doc(db, 'confessions', shadow.id), {
                                 commentCount: increment(-1)
                             });
                         } catch (err) {
                             console.error("Failed to delete comment", err);
                             toast.error('Failed to delete comment from server');
                         }
                     })();
                 }} className="text-red-500 hover:text-red-400 text-[13px] font-medium transition-colors">Delete</button>
              )}
              <button onClick={() => {
                if (window.onReportComment) window.onReportComment(comment.id);
              }} className="text-gray-500 hover:text-white text-[13px] font-medium transition-colors">Report</button>
          </div>

          {showQuickReply && (
              <div className="mt-3 flex flex-col gap-2 bg-[#1a1a1a] p-2 rounded-xl border border-gray-800">
                 {selectedGif && (
                   <div className="relative mb-2 w-fit">
                     <img src={selectedGif.url} alt="Attachment" className="h-20 w-auto rounded-lg object-contain bg-[#111] border border-gray-800" />
                     <button type="button" onClick={() => setSelectedGif(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                       <X size={14} />
                     </button>
                   </div>
                 )}
                 {selectedImagePreview && (
                   <div className="relative mb-2 w-fit">
                     <img src={selectedImagePreview} alt="Attachment" className="h-20 w-auto rounded-lg object-contain bg-[#111] border border-gray-800" />
                     <button type="button" onClick={() => { setSelectedImageFile(null); setSelectedImagePreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                       <X size={14} />
                     </button>
                   </div>
                 )}
                 <div className="flex gap-2">
                     <input 
                        type="text" 
                        value={quickReplyText}
                        onChange={(e) => setQuickReplyText(e.target.value)}
                        placeholder={`Reply to ${authorName}...`}
                        className="flex-1 bg-transparent text-white text-[13px] px-2 outline-none"
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleQuickReply();
                        }}
                     />
                 </div>
                 <div className="flex items-center justify-between border-t border-gray-800/50 pt-2 mt-1">
                    <div className="flex items-center gap-1 relative">
                        <input type="file" accept="image/*,video/*" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                        <button type="button" onClick={() => setShowCamera(true)} className="p-1.5 text-gray-500 hover:text-white transition-colors"><Camera size={16} strokeWidth={1.5} /></button>
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="p-1.5 text-gray-500 hover:text-white transition-colors"><ImageIcon size={16} strokeWidth={1.5} /></button>
                        <button type="button" onClick={() => setShowGifPicker(!showGifPicker)} className={`mx-1 transition-colors font-bold text-[10px] border rounded flex items-center justify-center px-1 py-0.5 ${showGifPicker ? 'text-[#0BD9E5] border-[#0BD9E5] bg-[#0BD9E5]/10' : 'text-gray-500 hover:text-white border-gray-500'}`}>GIF</button>
                        
                        {showGifPicker && (
                          <div className="absolute z-50 left-0 top-8 w-64 bg-[#111] border border-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '250px' }}>
                            <div className="p-2 border-b border-gray-800 bg-black/50">
                                <input 
                                    type="text" 
                                    placeholder="Search GIFs..." 
                                    value={gifSearchQuery}
                                    onChange={(e) => setGifSearchQuery(e.target.value)}
                                    className="w-full bg-[#1a1a1a] text-white text-[12px] px-3 py-1.5 rounded-lg outline-none border border-gray-700 focus:border-[#0BD9E5]/50"
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 grid grid-cols-2 gap-2 custom-scrollbar">
                               {gifResults.map((gifUrl, i) => (
                                   <img 
                                      key={i} src={gifUrl} alt="gif" 
                                      onClick={() => { setSelectedGif({ url: gifUrl, aspectRatio: 1 }); setShowGifPicker(false); setSelectedImagePreview(null); setSelectedImageFile(null); }}
                                      className="w-full h-20 object-cover rounded-md cursor-pointer hover:opacity-80 transition-opacity border border-gray-800"
                                   />
                               ))}
                            </div>
                          </div>
                         )}
                    </div>
                    <button 
                        onClick={handleQuickReply}
                        disabled={(!quickReplyText.trim() && !selectedImageFile && !selectedGif) || isSubmitting}
                        className="bg-[#0BD9E5]/10 text-[#0BD9E5] px-4 py-1.5 rounded-full text-[12px] font-bold disabled:opacity-50 min-w-[60px] flex justify-center items-center"
                    >
                        {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : 'Reply'}
                    </button>
                 </div>
                 
                 <CameraModal 
                    isOpen={showCamera} 
                    onClose={() => setShowCamera(false)} 
                    onCapture={(file) => {
                        setSelectedImagePreview(URL.createObjectURL(file));
                        setSelectedImageFile(file);
                        setSelectedGif(null);
                        setShowCamera(false);
                    }} 
                 />
              </div>
          )}

          {comment.replies && comment.replies.length > 0 && (
             <div className="mt-2">
               <button 
                 onClick={() => setIsRepliesExpanded(!isRepliesExpanded)}
                 className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-[13px] font-medium transition-colors mb-2"
               >
                 <div className="w-6 h-[1px] bg-purple-500/50"></div>
                 {isRepliesExpanded 
                   ? (depth >= 2 ? 'Hide Thread' : 'Hide Replies') 
                   : (depth >= 2 ? `View Thread (${comment.replies.length})` : `View ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`)}
               </button>
               
               {isRepliesExpanded && (
                 <div className={depth >= 2 ? 'mt-2 -ml-11' : 'mt-2 pl-4 border-l-2 border-gray-800'}>
                   {comment.replies.map(reply => (
                      <CommentItem 
                         key={reply.id} 
                         comment={reply} 
                         shadow={shadow} 
                         onReply={onReply}
                         isReply={true}
                         depth={depth + 1}
                         onDeleteComment={onDeleteComment}
                         onReplyAdded={onReplyAdded}
                      />
                   ))}
                 </div>
               )}
             </div>
          )}
        </div>
      </div>
   );
};

export const ShadowPostView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [commentReportModalVisible, setCommentReportModalVisible] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  useEffect(() => {
    window.onReportComment = (commentId) => {
      setSelectedCommentId(commentId);
      setCommentReportModalVisible(true);
    };
    return () => { delete window.onReportComment; };
  }, []);
  
  const [shadow, setShadow] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);
  
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [gifResults, setGifResults] = useState([]);
  const [isSearchingGif, setIsSearchingGif] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const trendingGifs = [
    "https://media.giphy.com/media/l41YtZOb9EUABnuqA/giphy.gif",
    "https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif",
    "https://media.giphy.com/media/cfuL5gqFDreXxkWQ4o/giphy.gif",
    "https://media.giphy.com/media/f31DK1KpGsyMU/giphy.gif",
  ];

  useEffect(() => {
      if (!showGifPicker) return;
      if (gifSearchQuery.trim() === '') {
          setGifResults(trendingGifs);
          return;
      }
      const fetchGifs = async () => {
          setIsSearchingGif(true);
          try {
              const apiKey = process.env.REACT_APP_GIPHY_API_KEY || 'MkLDt7E3pR3qJnqYvjnAbGRTjq5zmQ1g';
              const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(gifSearchQuery)}&limit=20`);
              if (res.ok) {
                 const data = await res.json();
                 const urls = data.data.map(r => r.images.fixed_height.url);
                 setGifResults(urls);
              } else {
                 setGifResults(trendingGifs);
              }
          } catch(e) {
              setGifResults(trendingGifs);
          } finally {
              setIsSearchingGif(false);
          }
      };
      const timeoutId = setTimeout(fetchGifs, 500);
      return () => clearTimeout(timeoutId);
  }, [gifSearchQuery, showGifPicker]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const handleReplyAddedLocally = (parentId, newReply) => {
    const addReplyToTree = (commentsList) => {
        return commentsList.map(c => {
            if (c.id === parentId) {
                return { ...c, replies: [...(c.replies || []), newReply] };
            }
            if (c.replies && c.replies.length > 0) {
                return { ...c, replies: addReplyToTree(c.replies) };
            }
            return c;
        });
    };
    setComments(prev => addReplyToTree(prev));
  };

  const handleDeleteCommentLocally = (commentId) => {
      const removeCommentFromTree = (commentsList) => {
          return commentsList.filter(c => c.id !== commentId).map(c => {
              if (c.replies && c.replies.length > 0) {
                  return { ...c, replies: removeCommentFromTree(c.replies) };
              }
              return c;
          });
      };
      setComments(prev => removeCommentFromTree(prev));
      setShadow(prev => ({ ...prev, commentCount: Math.max(0, (prev.commentCount || 1) - 1) }));
  };

  const [error, setError] = useState(false);
  
  const [likedConf, setLikedConf] = useState(new Set());
  const [userCollege, setUserCollege] = useState('');
  const [isRestricted, setIsRestricted] = useState(false);

  const currentUserAvatar = currentUser ? getAvatarForShadow(getAnonymousName(currentUser.uid)) : null;

  useEffect(() => {
    if (currentUser) {
      getDoc(doc(db, 'users', currentUser.uid)).then(d => {
        if (d.exists()) setUserCollege(d.data().college || '');
      });
      getDocs(query(collection(db, 'users', currentUser.uid, 'likedConf'))).then(snap => {
        setLikedConf(new Set(snap.docs.map(doc => doc.id)));
      });
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchShadowAndComments = async () => {
      try {
        setLoading(true);
        const shadowSnap = await getDoc(doc(db, 'confessions', id));
        if (!shadowSnap.exists()) {
          setError(true);
          return;
        }

        const data = { id: shadowSnap.id, ...shadowSnap.data() };
        
        if (data.visibility === 'campus' && currentUser) {
            const uSnap = await getDoc(doc(db, 'users', currentUser.uid));
            const college = uSnap.exists() ? uSnap.data().college : '';
            if (college !== data.college) {
                setIsRestricted(true);
                setShadow(data);
                setLoading(false);
                return;
            }
        }

        setShadow(data);

        const q = query(
            collection(db, 'comments'),
            where('confessionId', '==', id),
            orderBy('createdAt', 'desc')
        );
        const commentsSnap = await getDocs(q);
        
        const allComments = commentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const rootComments = [];
        const replyMap = {};
        
        allComments.forEach(c => {
           if (c.parentId) {
               if (!replyMap[c.parentId]) replyMap[c.parentId] = [];
               replyMap[c.parentId].push(c);
           } else {
               rootComments.push(c);
           }
        });
        
        rootComments.sort((a,b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        Object.keys(replyMap).forEach(k => {
             replyMap[k].sort((a,b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
        });

        const buildReplies = (parentId) => {
             const children = replyMap[parentId] || [];
             return children.map(child => ({
                  ...child,
                  replies: buildReplies(child.id)
             }));
        };

        const builtComments = rootComments.map(root => ({
             ...root,
             replies: buildReplies(root.id)
        }));
        setComments(builtComments);

      } catch (err) {
        console.error("Error fetching shadow data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchShadowAndComments();
  }, [id, currentUser]);

  const handleAddComment = async (e) => {
    e?.preventDefault();
    if ((!commentText.trim() && !selectedImageFile && !selectedGif) || isCommenting || !currentUser) return;
    
    setIsCommenting(true);
    let finalImageUrl = null;

    try {
      if (selectedImageFile) {
        setUploading(true);
        const storageRef = ref(storage, `shadow_comments/${Date.now()}_${selectedImageFile.name}`);
        const uploadTask = await uploadBytesResumable(storageRef, selectedImageFile);
        finalImageUrl = await getDownloadURL(uploadTask.ref);
        setUploading(false);
      }

      const parentId = replyingTo ? (replyingTo.parentId || replyingTo.id) : null;
      const newComment = {
        confessionId: shadow.id,
        parentId: parentId,
        userId: currentUser.uid,
        content: commentText.trim(),
        createdAt: serverTimestamp(),
        likes: 0,
        imageUrl: finalImageUrl,
        gif: selectedGif ? { url: selectedGif.url, id: selectedGif.id, aspectRatio: selectedGif.aspectRatio } : null
      };
      
      const docRef = await addDoc(collection(db, 'comments'), newComment);
      
      const addedComment = { id: docRef.id, ...newComment, createdAt: new Date() };
      
      if (parentId) {
         setComments(prev => prev.map(c => {
             if (c.id === parentId) {
                return { ...c, replies: [...(c.replies || []), addedComment] };
             }
             return c;
         }));
      } else {
         setComments(prev => [addedComment, ...prev]);
      }
      
      // Notification Logic
      if (currentUser.uid !== shadow.userId) {
          await addDoc(collection(db, 'users', shadow.userId, 'notifications'), {
              type: 'comment',
              fromUserId: currentUser.uid,
              confessionId: shadow.id,
              commentId: docRef.id,
              content: commentText.trim(),
              parentCommentId: parentId || null,
              createdAt: new Date(),
              read: false,
              image: shadow.imageUrl || null
          });
      }
      
      if (parentId) {
          const parentSnap = await getDoc(doc(db, 'comments', parentId));
          if (parentSnap.exists() && parentSnap.data().userId !== currentUser.uid && parentSnap.data().userId !== shadow.userId) {
              await addDoc(collection(db, 'users', parentSnap.data().userId, 'notifications'), {
                  type: 'comment_reply',
                  fromUserId: currentUser.uid,
                  confessionId: shadow.id,
                  commentId: docRef.id,
                  content: commentText.trim(),
                  parentCommentId: parentId,
                  createdAt: new Date(),
                  read: false,
                  image: shadow.imageUrl || null
              });
          }
      }
      
      toast.success(parentId ? "Reply added" : "Comment added");

      setCommentText('');
      setReplyingTo(null);
      removeMedia();
      
      await updateDoc(doc(db, 'confessions', shadow.id), {
        commentCount: increment(1)
      });
      setShadow(prev => ({ ...prev, commentCount: (prev.commentCount || 0) + 1 }));
      
    } catch (err) {
      console.error(err);
      toast.error('Failed to post comment');
      setUploading(false);
    } finally {
      setIsCommenting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      setSelectedImagePreview(URL.createObjectURL(file));
      setSelectedGif(null);
      setShowGifPicker(false);
    }
  };

  const removeMedia = () => {
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
    setSelectedGif(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleGifClick = (gifUrl, e) => {
    e?.preventDefault();
    setSelectedGif({ url: gifUrl, id: Date.now().toString(), aspectRatio: 1 });
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
    setShowGifPicker(false);
  };

  const handleDeleteShadow = (shadowId) => {
      navigate('/');
  };

  const handleReportConfession = async () => {
    if (!reportReason.trim() || submittingReport || !currentUser) return;
    setSubmittingReport(true);
    try {
      await addDoc(collection(db, 'confessionReports'), {
        confessionId: shadow.id, userId: currentUser.uid, reason: reportReason.trim(), createdAt: new Date(), status: 'pending'
      });
      setReportModalVisible(false); setReportReason(''); setSuccessModalVisible(true);
    } catch (err) { alert('Report failed'); } finally { setSubmittingReport(false); }
  };

  const handleReportComment = async () => {
    if (!reportReason.trim() || submittingReport || !selectedCommentId || !currentUser) return;
    setSubmittingReport(true);
    try {
      await addDoc(collection(db, 'commentReports'), {
        confessionId: shadow.id, commentId: selectedCommentId, userId: currentUser.uid, reason: reportReason.trim(), createdAt: new Date(), status: 'pending'
      });
      setCommentReportModalVisible(false); setReportReason(''); setSelectedCommentId(null); setSuccessModalVisible(true);
    } catch (err) { alert('Comment report failed'); } finally { setSubmittingReport(false); }
  };

  const handleLikeUpdate = (shadowId, isLiked) => {
    setLikedConf(prev => {
      const newSet = new Set(prev);
      if (isLiked) newSet.add(shadowId);
      else newSet.delete(shadowId);
      return newSet;
    });
  };

  if (loading) {
    return (
      <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
      </main>
    );
  }

  if (error || !shadow) {
    return (
      <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 items-center justify-center p-8 text-center">
        <h2 className="text-xl font-bold text-white mb-2">Shadow not found</h2>
        <p className="text-gray-400 mb-6">This shadow may have been deleted or you don't have permission to view it.</p>
        <button onClick={() => navigate(-1)} className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-full transition-colors">
          Go Back
        </button>
      </main>
    );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col pb-20 sm:pb-0 shrink-0">
      <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center h-[60px] px-4">
        <button 
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2"
        >
          <ChevronLeft size={24} className="text-white" />
        </button>
        <h1 className="text-xl font-extrabold text-white">Shadow</h1>
      </div>

      <div className="p-4 sm:p-5">
        <ShadowCard 
          shadow={shadow}
          isLiked={likedConf.has(shadow.id)}
          onLikeUpdate={handleLikeUpdate}
          onDelete={handleDeleteShadow}
          onReport={() => setReportModalVisible(true)}
        />
        <div className="mt-6 border-t border-gray-800 pt-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <MessageCircle size={20} className="text-[#0BD9E5]" />
              Comments
            </h3>

            {/* Comment Input */}
            <div className="flex gap-3 mb-8 items-start relative">
              <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center shrink-0 border border-gray-700 overflow-hidden mt-1">
                {currentUserAvatar ? (
                    <img src={currentUserAvatar} className="w-full h-full object-cover" alt="Me" />
                ) : <User size={18} className="text-gray-500" />}
              </div>
              <div className="flex-1 flex flex-col bg-[#121212] border border-gray-800/80 rounded-xl overflow-hidden focus-within:border-gray-600 transition-colors">
                
                {/* Media Previews */}
                {(selectedImagePreview || selectedGif) && (
                  <div className="relative mb-2 ml-3 mt-3 w-fit">
                    <img src={selectedImagePreview || selectedGif.url} alt="Attachment" className="h-24 w-auto rounded-lg object-contain bg-[#111] border border-gray-800" />
                    <button type="button" onClick={removeMedia} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                      <X size={14} />
                    </button>
                  </div>
                )}

                <textarea 
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={replyingTo ? `Replying to @${getAnonymousName(replyingTo.userId)}...` : "Add a comment..."}
                  className="w-full bg-transparent px-4 py-3 text-[15px] text-white placeholder-gray-500 focus:outline-none resize-none min-h-[60px]"
                />
                <div className="flex items-center justify-between px-2 py-2 border-t border-gray-800/50 bg-[#0d0d0d]">
                   <div className="flex items-center gap-1">
                      
                      <input type="file" accept="image/*,video/*" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                      
                      <button type="button" onClick={() => setShowCamera(true)} className="p-2 text-gray-500 hover:text-white transition-colors"><Camera size={20} strokeWidth={1.5} /></button>
                      <button onClick={() => fileInputRef.current?.click()} className="p-2 text-gray-500 hover:text-white transition-colors"><ImageIcon size={20} strokeWidth={1.5} /></button>
                      <button onClick={() => setShowGifPicker(!showGifPicker)} className={`mx-1 transition-colors font-bold text-[11px] border rounded flex items-center justify-center px-1.5 py-0.5 ${showGifPicker ? 'text-[#0BD9E5] border-[#0BD9E5] bg-[#0BD9E5]/10' : 'text-gray-500 hover:text-white border-gray-500'}`}>GIF</button>
                   </div>
                   <div className="flex items-center gap-3">
                      {replyingTo && (
                         <button onClick={() => setReplyingTo(null)} className="text-gray-500 text-sm hover:text-white font-medium">Cancel</button>
                      )}
                      <button 
                        onClick={handleAddComment}
                        disabled={(!commentText.trim() && !selectedImageFile && !selectedGif) || isCommenting || uploading}
                        className="w-8 h-8 flex items-center justify-center bg-[#0BD9E5] text-black rounded-full disabled:opacity-30 disabled:bg-gray-700 disabled:cursor-not-allowed hover:bg-[#0BD9E5]/80 transition-colors"
                      >
                        {isCommenting || uploading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div> : <Send size={14} className="ml-0.5" />}
                      </button>
                   </div>
                </div>

                {showGifPicker && (
                  <div className="absolute top-[80px] right-0 mt-2 z-50 w-72 bg-[#111] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-96">
                    <div className="p-3 border-b border-gray-800 flex justify-between items-center bg-black/50 backdrop-blur-md">
                        <input 
                            type="text" 
                            placeholder="Search GIFs..." 
                            value={gifSearchQuery}
                            onChange={(e) => setGifSearchQuery(e.target.value)}
                            className="bg-transparent border-none text-white text-sm focus:outline-none w-full mr-2"
                            autoFocus
                        />
                        <button onClick={() => setShowGifPicker(false)} className="text-gray-500 hover:text-white shrink-0"><X size={16}/></button>
                    </div>
                    <div className="p-2 grid grid-cols-2 gap-2 overflow-y-auto custom-scrollbar relative" style={{ minHeight: '150px', maxHeight: '250px' }}>
                       {isSearchingGif ? (
                           <div className="absolute inset-0 flex items-center justify-center">
                               <Loader2 size={24} className="text-[#0BD9E5] animate-spin" />
                           </div>
                       ) : (
                           gifResults.map((gifUrl, i) => (
                               <img 
                                  key={i} src={gifUrl} alt="gif" 
                                  onClick={(e) => handleGifClick(gifUrl, e)}
                                  className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border border-gray-800"
                               />
                           ))
                       )}
                     </div>
                  </div>
                )}
              </div>
            </div>

            {/* Comments List */}
            <div className="flex flex-col gap-6">
              {comments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No comments yet. Be the first to start the conversation!</p>
                </div>
              ) : (
                comments.map(comment => (
                   <CommentItem 
                      key={comment.id} 
                      comment={comment} 
                      shadow={shadow} 
                      onReply={(c) => setReplyingTo(c)} 
                      onDeleteComment={handleDeleteCommentLocally}
                      onReplyAdded={handleReplyAddedLocally}
                   />
                ))
              )}
            </div>
          </div>

        {/* Report Confession Modal */}
        {reportModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
            <div className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-white mb-4 text-center">Report Shadow</h3>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Why are you reporting this?"
                className="w-full bg-[#1a1a1a] border border-gray-800 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none h-28 mb-2"
                autoFocus
              />
              <div className="flex justify-between gap-3 mt-4">
                 <button onClick={() => { setReportModalVisible(false); setReportReason(''); }} className="flex-1 px-4 py-3 bg-[#2a2a2a] text-gray-400 hover:text-white font-medium rounded-xl transition-colors">Cancel</button>
                 <button onClick={handleReportConfession} disabled={reportReason.trim().length < 10 || submittingReport} className="flex-1 px-4 py-3 bg-[#1a1a1a] border border-gray-600 text-white font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors">
                    {submittingReport ? 'Sending...' : 'Submit'}
                 </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Comment Modal */}
        {commentReportModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
            <div className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-white mb-4 text-center">Report Comment</h3>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Why are you reporting this comment?"
                className="w-full bg-[#1a1a1a] border border-gray-800 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none h-28 mb-2"
                autoFocus
              />
              <div className="flex justify-between gap-3 mt-4">
                 <button onClick={() => { setCommentReportModalVisible(false); setReportReason(''); setSelectedCommentId(null); }} className="flex-1 px-4 py-3 bg-[#2a2a2a] text-gray-400 hover:text-white font-medium rounded-xl transition-colors">Cancel</button>
                 <button onClick={handleReportComment} disabled={reportReason.trim().length < 10 || submittingReport} className="flex-1 px-4 py-3 bg-[#1a1a1a] border border-gray-600 text-white font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors">
                    {submittingReport ? 'Sending...' : 'Submit'}
                 </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {successModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
            <div className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-sm p-6 flex flex-col items-center text-center">
              <h3 className="text-lg font-bold text-white mb-2">Thank You</h3>
              <p className="text-gray-400 text-[15px] mb-6">Your report has been submitted successfully. We'll review it and take action if needed.</p>
              <button onClick={() => setSuccessModalVisible(false)} className="w-full py-3 bg-[#1a1a1a] border border-gray-600 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors">
                 Got it
              </button>
            </div>
          </div>
        )}

        <CameraModal 
            isOpen={showCamera} 
            onClose={() => setShowCamera(false)} 
            onCapture={(file) => {
                setSelectedImagePreview(URL.createObjectURL(file));
                setSelectedImageFile(file);
                setSelectedGif(null);
                setShowCamera(false);
            }} 
        />
      </div>
    </main>
  );
};
